import React, { useState } from 'react';
import { CostExplanationModal } from '../../curriculum/components/CostExplanationModal';
import { useTranslation } from '../../../shared/i18n';

/**
 * SubscriptionRequiredModal - Shows when user clicks on a pro activity without proper subscription
 */
const SubscriptionRequiredModal = ({ activityName, onClose, isTestMode }) => {
  const { t } = useTranslation();

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      {...(isTestMode && { 'data-testid': 'subscription-required-modal' })}
    >
      <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-star-fill text-warning me-2"></i>
              {t('activityTemplateSelector.subscription_required_modal.title')}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label={t('activityTemplateSelector.subscription_required_modal.close')}
              {...(isTestMode && { 'data-testid': 'close-subscription-modal-btn' })}
            ></button>
          </div>
          <div className="modal-body">
            <p className="mb-3" {...(isTestMode && { 'data-testid': 'subscription-explanation-text' })}>
              <strong>{activityName}</strong> {t('activityTemplateSelector.subscription_required_modal.message_prefix')} <span className="badge bg-primary">Standard</span> {t('activityTemplateSelector.subscription_required_modal.message_or')} <span className="badge bg-warning text-dark">Pro</span>.
            </p>
            <p className="mb-0 text-muted">
              {t('activityTemplateSelector.subscription_required_modal.upgrade_prompt')}
            </p>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onClose}
              {...(isTestMode && { 'data-testid': 'subscription-understood-btn' })}
            >
              {t('activityTemplateSelector.subscription_required_modal.understood')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ActivityTemplateSelector - Displays a list of available activity templates
 * for the guardian to choose from
 */
const ActivityTemplateSelector = ({ templates, familySubscription, onSelect, onCancel, isTestMode }) => {
  const { t } = useTranslation();
  const [costModalData, setCostModalData] = useState(null);
  const [subscriptionModalData, setSubscriptionModalData] = useState(null);

  const handleCostBadgeClick = (e, template) => {
    e.stopPropagation();
    setCostModalData({
      activityName: template.name,
      activityType: template.activity_type
    });
  };

  const handleProBadgeClick = (e, template) => {
    e.stopPropagation();
    setSubscriptionModalData({
      activityName: template.name
    });
  };

  const closeCostModal = () => {
    setCostModalData(null);
  };

  const closeSubscriptionModal = () => {
    setSubscriptionModalData(null);
  };

  // Check if family has required subscription for a template
  const hasRequiredSubscription = (template) => {
    if (!template.requires_subscription) {
      return true; // No subscription required
    }
    return template.requires_subscription.includes(familySubscription);
  };

  // Check if activity is selectable (based on subscription status and free settings)
  const isActivitySelectable = (template) => {
    // Use the selectable field from the API if available
    if (template.selectable !== undefined) {
      return template.selectable;
    }
    // Fallback to requires_subscription check for backwards compatibility
    return hasRequiredSubscription(template);
  };

  // Handle template selection - check selectability first
  const handleTemplateClick = (template) => {
    if (!isActivitySelectable(template)) {
      setSubscriptionModalData({ activityName: template.name });
      return;
    }
    onSelect(template);
  };

  return (
    <div className="activity-template-selector">
      {/* Header */}
      <div className="mb-4">
        <button
          type="button"
          className="btn btn-link text-secondary p-0 mb-3"
          onClick={onCancel}
          {...(isTestMode && { 'data-testid': 'back-to-curriculum-btn' })}
        >
          <i className="bi bi-arrow-left me-2"></i>
          {t('activityTemplateSelector.back_to_curriculum')}
        </button>
        <h2 className="h1 fw-bold mb-2">{t('activityTemplateSelector.add_new_activity')}</h2>
        <p className="text-muted">{t('activityTemplateSelector.select_educational_activity')}</p>
      </div>

      {/* Activity Selection Card */}
      <div className="card border rounded-3">
        <div className="card-body p-4">
          {/* Activity Type Label */}
          <div className="mb-4">
            <label className="form-label fw-semibold fs-5">{t('activityTemplateSelector.select_activity_type')}</label>
          </div>

          {/* Activity List */}
          <div className="d-flex flex-column gap-3">
            {templates.map((template) => {
              const selectable = isActivitySelectable(template);
              return (
              <div
                key={template.key}
                className={`border border-2 rounded-3 p-3 activity-card ${!selectable ? 'opacity-50' : ''}`}
                {...(isTestMode && { 'data-testid': `activity-card-${template.key}` })}
              >
                <button
                  type="button"
                  className="text-decoration-none activity-card-link d-block w-100 text-start border-0 bg-transparent"
                  onClick={() => handleTemplateClick(template)}
                  disabled={!selectable}
                  aria-label={`Select ${template.name}`}
                  {...(isTestMode && { 'data-testid': `select-${template.key}-btn`, 'data-react-ready': 'true' })}
                >
                  <div className="d-flex align-items-start">
                    {/* Activity Icon */}
                    <div className="flex-shrink-0 me-3">
                      <div className="rounded-2 overflow-hidden" style={{ width: '64px', height: '64px' }}>
                        <img
                          src={template.icon_url}
                          alt={template.name}
                          className="w-100 h-100 object-fit-cover"
                          onError={(e) => {
                            e.target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Crect width='64' height='64' fill='%23e9ecef'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='24' fill='%236c757d'%3E${template.name[0]}%3C/text%3E%3C/svg%3E`;
                          }}
                        />
                      </div>
                    </div>

                    {/* Activity Details */}
                    <div className="flex-grow-1">
                      <h3 className="h5 fw-bold mb-2 text-dark">
                        {template.name}
                        {!template.free && (
                          <span
                            className="badge bg-warning text-dark ms-2 pro-badge"
                            onClick={(e) => handleProBadgeClick(e, template)}
                            style={{ cursor: 'pointer', fontSize: '0.7rem', verticalAlign: 'middle' }}
                            {...(isTestMode && { 'data-testid': `pro-badge-${template.key}` })}
                          >
                            <i className="bi bi-star-fill me-1"></i>
                            {t('common.badge_pro')}
                          </span>
                        )}
                        {template.free && (
                          <span
                            className="badge bg-success ms-2"
                            style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}
                            {...(isTestMode && { 'data-testid': `free-badge-${template.key}` })}
                          >
                            {t('common.badge_free')}
                          </span>
                        )}
                      </h3>
                      <p className="text-muted mb-2">{template.description}</p>

                      {/* Tags */}
                      <div className="d-flex gap-2 flex-wrap">
                        {template.tags.map((tag, index) => {
                          const colors = template.tag_colors?.[tag] || { bg: 'bg-secondary', text: 'text-white' };
                          const isCostTag = tag === 'Tốn thêm tiền';
                          return (
                            <span
                              key={tag}
                              className={`badge rounded-pill ${colors.bg} ${colors.text} px-3 py-2 ${isCostTag ? 'cost-badge' : ''}`}
                              onClick={isCostTag ? (e) => handleCostBadgeClick(e, template) : undefined}
                              style={isCostTag ? { cursor: 'pointer' } : undefined}
                              {...(isTestMode && isCostTag && { 'data-testid': `cost-badge-${template.key}` })}
                            >
                              {tag}
                              {isCostTag && <i className="bi bi-info-circle ms-1"></i>}
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {/* Arrow Icon */}
                    <div className="flex-shrink-0 ms-3 align-self-center">
                      <i className="bi bi-arrow-right text-primary" style={{ fontSize: '1.5rem' }}></i>
                    </div>
                  </div>
                </button>
              </div>
            );
            })}
          </div>
        </div>
      </div>

      {/* Cost Explanation Modal - uses shared component from curriculum */}
      {costModalData && (
        <CostExplanationModal
          activityName={costModalData.activityName}
          activityType={costModalData.activityType}
          onClose={closeCostModal}
        />
      )}

      {/* Subscription Required Modal */}
      {subscriptionModalData && (
        <SubscriptionRequiredModal
          activityName={subscriptionModalData.activityName}
          onClose={closeSubscriptionModal}
          isTestMode={isTestMode}
        />
      )}

      <style>{`
        .activity-card {
          transition: all 0.2s ease;
        }

        .activity-card-link:hover .activity-card,
        .activity-card:has(.activity-card-link:hover) {
          border-color: var(--bs-primary) !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          cursor: pointer;
        }

        .activity-card-link:focus,
        .activity-card:has(.activity-card-link:focus) {
          border-color: var(--bs-primary) !important;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
          outline: none;
        }

        .cost-badge:hover {
          opacity: 0.8;
        }

        .pro-badge:hover {
          opacity: 0.8;
        }
      `}</style>
    </div>
  );
};

export default ActivityTemplateSelector;
